import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { buildShip, cellToWorld, type ShipRig } from "./ship";
import { buildGhostMesh, buildLootMesh, CELL, LAYER_H } from "./loot";

/**
 * The 3D stage: sunset sea, the brig, the cargo. Owns the renderer, camera,
 * lights and animation loop; exposes a small imperative API the game logic
 * drives (placement ghosts, loot meshes, the reveal replay). All game RULES
 * live in src/game — this module only draws what it is told.
 */

export interface LootPiece {
  uid: number;
  key: string;
  x: number; y: number; z: number;
  w: number; d: number; h: number;
}

interface Tween { start: number; dur: number; tick: (k: number) => void; done?: () => void }

const easeOutCubic = (k: number) => 1 - Math.pow(1 - k, 3);

const reducedMotion = typeof matchMedia !== "undefined" &&
  matchMedia("(prefers-reduced-motion: reduce)").matches;

export class Stage {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly controls: OrbitControls;

  private shipRig: ShipRig | null = null;
  private shipGroup = new THREE.Group();   // ship + cargo, bobs together
  private lootGroup = new THREE.Group();
  private meshes = new Map<number, THREE.Group>();
  private ghost: { group: THREE.Group; setValid: (ok: boolean) => void; w: number; d: number } | null = null;
  private cols = 1; private rows = 1;
  private tweens: Tween[] = [];
  private raycaster = new THREE.Raycaster();
  private sea: THREE.Mesh;
  private seaBase: Float32Array | null = null;
  private sun: THREE.DirectionalLight;
  private t0 = performance.now();
  private bump = 0;

  constructor(host: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.22;
    host.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(42, 1, 0.1, 220);
    this.camera.position.set(7, 8, 9);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enablePan = false;
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.minPolarAngle = 0.32;
    this.controls.maxPolarAngle = 1.25;
    this.controls.target.set(0, 0.4, 0);

    /* warm low sun + cool fill + the stern lantern */
    this.sun = new THREE.DirectionalLight(0xffd9a0, 3.1);
    this.sun.position.set(9, 11, 6);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.bias = -0.0006;
    this.scene.add(this.sun);
    this.scene.add(new THREE.AmbientLight(0x9db2c6, 1.05));
    const hemi = new THREE.HemisphereLight(0xffc98a, 0x123a48, 0.7);
    this.scene.add(hemi);
    const lamp = new THREE.PointLight(0xffb45e, 16, 13, 2);
    lamp.position.set(0, 2.6, -4.5);
    this.scene.add(lamp);

    /* the sea — vertex-bobbed plane whose edge melts into the sunset fog,
       so the page's sky gradient becomes the horizon behind it */
    const seaGeo = new THREE.PlaneGeometry(96, 96, 56, 56);
    seaGeo.rotateX(-Math.PI / 2);
    const seaMat = new THREE.MeshPhongMaterial({ color: 0x175563, specular: 0xb27c40, shininess: 72 });
    this.sea = new THREE.Mesh(seaGeo, seaMat);
    this.sea.position.y = -0.58;
    this.sea.receiveShadow = true;
    this.scene.add(this.sea);
    if (!reducedMotion) this.seaBase = new Float32Array(seaGeo.attributes.position.array);
    this.scene.fog = new THREE.Fog(0xd07c4c, 16, 48);

    this.shipGroup.add(this.lootGroup);
    this.scene.add(this.shipGroup);

    this.host = host;
    this.resize();
    addEventListener("resize", () => this.resize());

    this.renderer.setAnimationLoop(() => this.frame());
  }

  private host: HTMLElement;

  /** Re-fit the canvas to its host (also called when the quay dock resizes),
   *  re-deriving the camera range so the grid stays fully in frame. */
  resize(): void {
    const w = this.host.clientWidth, h = Math.max(this.host.clientHeight, 1);
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    if (this.shipRig) {
      const dist = this.fitDistance();
      this.controls.minDistance = dist * 0.62;
      this.controls.maxDistance = dist * 1.9;
      const dir = this.camera.position.clone().sub(this.controls.target).normalize();
      this.camera.position.copy(this.controls.target).addScaledVector(dir, dist);
      this.controls.update();
    }
  }

  /* ---------------- island setup ---------------- */

  /** Camera distance that keeps the whole roped grid in frame — portrait
   *  viewports need extra range because the fov is vertical. */
  private fitDistance(): number {
    const span = Math.max(this.cols, this.rows + 4) * CELL;
    const aspect = Math.max(this.camera.aspect, 0.01);
    const portrait = Math.min(2.3, Math.max(1, 1.18 / Math.pow(aspect, 0.95)));
    return span * 1.42 * portrait;
  }

  setIsland(cols: number, rows: number, maxH: number): void {
    this.cols = cols; this.rows = rows;
    if (this.shipRig) {
      this.shipGroup.remove(this.shipRig.group);
      this.shipRig.group.traverse((o) => {
        if (o instanceof THREE.Mesh) o.geometry.dispose();
      });
    }
    this.clearLoot();
    this.hideGhost();
    this.shipRig = buildShip(cols, rows, maxH);
    this.shipGroup.add(this.shipRig.group);

    const dist = this.fitDistance();
    this.controls.minDistance = dist * 0.62;
    this.controls.maxDistance = dist * 1.9;
    const az = -0.62, pol = 1.02; // the reference three-quarter view
    this.camera.position.set(
      Math.sin(az) * Math.sin(pol) * dist,
      Math.cos(pol) * dist + 0.4,
      Math.cos(az) * Math.sin(pol) * dist);
    this.controls.target.set(0, 0.4, 0);
    this.controls.update();

    const s = Math.max(cols, rows + 4) * CELL * 0.9 + 4;
    const cam = this.sun.shadow.camera;
    cam.left = -s; cam.right = s; cam.top = s; cam.bottom = -s;
    cam.updateProjectionMatrix();
  }

  /* ---------------- loot meshes ---------------- */

  addLoot(p: LootPiece, animate = true): void {
    const g = buildLootMesh(p.key, p.w, p.d);
    g.userData.uid = p.uid;
    const [wx, wz] = cellToWorld(this.cols, this.rows, p.x, p.y);
    const cx = wx + (p.w - 1) * CELL * 0.5;
    const cz = wz + (p.d - 1) * CELL * 0.5;
    const ty = p.z * LAYER_H;
    g.position.set(cx, ty, cz);
    this.lootGroup.add(g);
    this.meshes.set(p.uid, g);
    if (animate && !reducedMotion) {
      g.position.y = ty + 2.4;
      this.tween(170, (k) => { g.position.y = ty + 2.4 * (1 - easeOutCubic(k)); }, () => {
        g.position.y = ty;
        this.bump = Math.min(this.bump + 0.5, 1);
        this.tween(130, (k) => {
          const s = 1 + 0.08 * Math.sin(k * Math.PI);
          g.scale.set(s, 1 / s, s);
        }, () => g.scale.set(1, 1, 1));
      });
    }
  }

  removeLoot(uid: number): void {
    const g = this.meshes.get(uid);
    if (!g) return;
    this.meshes.delete(uid);
    this.lootGroup.remove(g);
    g.traverse((o) => { if (o instanceof THREE.Mesh) o.geometry.dispose(); });
  }

  clearLoot(): void {
    for (const uid of [...this.meshes.keys()]) this.removeLoot(uid);
  }

  /** Pop a piece in for the reveal replay (slightly faster, golden flash). */
  revealLoot(p: LootPiece): void {
    this.addLoot(p, !reducedMotion);
  }

  /* ---------------- the placement ghost ---------------- */

  setGhost(key: string | null, w = 1, d = 1): void {
    if (this.ghost) {
      this.lootGroup.remove(this.ghost.group);
      this.ghost.group.traverse((o) => { if (o instanceof THREE.Mesh) o.geometry.dispose(); });
      this.ghost = null;
    }
    this.clearGlow();
    if (key) {
      const gh = buildGhostMesh(key, w, d);
      gh.group.visible = false;
      gh.group.traverse((o) => { o.castShadow = false; o.receiveShadow = false; });
      this.lootGroup.add(gh.group);
      this.ghost = { ...gh, w, d };
    }
  }

  moveGhost(x: number, y: number, z: number, ok: boolean, heightFault = false): void {
    if (!this.ghost) return;
    const { group, setValid, w, d } = this.ghost;
    const [wx, wz] = cellToWorld(this.cols, this.rows, x, y);
    group.visible = true;
    group.position.set(wx + (w - 1) * CELL * 0.5, z * LAYER_H + 0.02, wz + (d - 1) * CELL * 0.5);
    setValid(ok);
    this.clearGlow();
    if (this.shipRig) {
      for (let dy = 0; dy < d; dy++) for (let dx = 0; dx < w; dx++) {
        const gy = y + dy, gx = x + dx;
        const hl = this.shipRig.glow[gy]?.[gx];
        if (hl) {
          const m = hl.material as THREE.MeshBasicMaterial;
          m.color.set(ok ? 0xffd97a : 0xe0644d);
          m.opacity = 0.5;
        }
      }
      const lm = this.shipRig.limit.userData.lineMat as THREE.LineBasicMaterial;
      lm.color.set(heightFault ? 0xe0644d : 0xc9a061);
      lm.opacity = heightFault ? 0.95 : 0.5;
    }
  }

  hideGhost(): void {
    if (this.ghost) this.ghost.group.visible = false;
    this.clearGlow();
  }

  private clearGlow(): void {
    if (!this.shipRig) return;
    for (const row of this.shipRig.glow) for (const hl of row) (hl.material as THREE.MeshBasicMaterial).opacity = 0;
    const lm = this.shipRig.limit.userData.lineMat as THREE.LineBasicMaterial;
    lm.color.set(0xc9a061);
    lm.opacity = 0.5;
  }

  /* ---------------- picking ---------------- */

  private castAt(clientX: number, clientY: number, targets: THREE.Object3D[]): THREE.Intersection | null {
    const r = this.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((clientX - r.left) / r.width) * 2 - 1,
      -((clientY - r.top) / r.height) * 2 + 1);
    this.raycaster.setFromCamera(ndc, this.camera);
    const hits = this.raycaster.intersectObjects(targets, true);
    return hits[0] ?? null;
  }

  /** The deck cell under the pointer (via tiles or the cargo stacked on them). */
  pickCell(clientX: number, clientY: number): { x: number; y: number } | null {
    if (!this.shipRig) return null;
    const hit = this.castAt(clientX, clientY, [...this.shipRig.tiles, this.lootGroup]);
    if (!hit) return null;
    const local = this.shipGroup.worldToLocal(hit.point.clone());
    const x = Math.floor(local.x / CELL + this.cols / 2);
    const y = Math.floor(local.z / CELL + this.rows / 2);
    if (x < 0 || y < 0 || x >= this.cols || y >= this.rows) return null;
    return { x, y };
  }

  /** The placed piece under the pointer, if any. */
  pickLoot(clientX: number, clientY: number): number | null {
    const hit = this.castAt(clientX, clientY, [this.lootGroup]);
    if (!hit) return null;
    let o: THREE.Object3D | null = hit.object;
    while (o && o.userData.uid === undefined) o = o.parent;
    return o ? (o.userData.uid as number) : null;
  }

  /** Screen position (CSS px in the viewport) of a cell center at a layer. */
  cellToScreen(x: number, y: number, z = 0): { x: number; y: number } {
    const [wx, wz] = cellToWorld(this.cols, this.rows, x, y);
    const v = new THREE.Vector3(wx, z * LAYER_H + 0.01, wz);
    this.shipGroup.localToWorld(v);
    v.project(this.camera);
    const r = this.renderer.domElement.getBoundingClientRect();
    return { x: r.left + ((v.x + 1) / 2) * r.width, y: r.top + ((1 - v.y) / 2) * r.height };
  }

  /** Wiggle a placed piece that refuses to come up (something rests on it). */
  shakeLoot(uid: number): void {
    const g = this.meshes.get(uid);
    if (!g || reducedMotion) return;
    const x0 = g.position.x;
    this.tween(240, (k) => { g.position.x = x0 + Math.sin(k * Math.PI * 4) * 0.06 * (1 - k); },
      () => { g.position.x = x0; });
  }

  setRevealMode(on: boolean): void {
    this.controls.autoRotate = on && !reducedMotion;
    this.controls.autoRotateSpeed = 0.7;
  }

  /* ---------------- frame loop ---------------- */

  private tween(dur: number, tick: (k: number) => void, done?: () => void): void {
    this.tweens.push({ start: performance.now(), dur, tick, done });
  }

  private frame(): void {
    const now = performance.now();
    const t = (now - this.t0) / 1000;

    for (let i = this.tweens.length - 1; i >= 0; i--) {
      const tw = this.tweens[i];
      const k = Math.min(1, (now - tw.start) / tw.dur);
      tw.tick(k);
      if (k >= 1) { this.tweens.splice(i, 1); tw.done?.(); }
    }

    if (!reducedMotion) {
      // the swell: ship bob + sea vertices
      this.bump = Math.max(0, this.bump - 0.04);
      this.shipGroup.position.y = Math.sin(t * 0.6) * 0.05 - this.bump * 0.05;
      this.shipGroup.rotation.z = Math.sin(t * 0.42) * 0.012;
      this.shipGroup.rotation.x = Math.cos(t * 0.31) * 0.008;
      if (this.seaBase) {
        const pos = (this.sea.geometry as THREE.PlaneGeometry).attributes.position;
        const arr = pos.array as Float32Array;
        for (let i = 0; i < pos.count; i++) {
          const x = this.seaBase[i * 3], z = this.seaBase[i * 3 + 2];
          arr[i * 3 + 1] = this.seaBase[i * 3 + 1] +
            Math.sin(x * 0.35 + t * 1.1) * 0.1 + Math.cos(z * 0.3 + t * 0.8) * 0.09;
        }
        pos.needsUpdate = true;
        this.sea.geometry.computeVertexNormals();
      }
    }

    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }
}
